import { requireConnection } from './connectivity';
import { Injectable } from '@angular/core';
import { TimingEditor } from './timing';

export const POSITIONS = ['year', 'month', 'day', 'hour'];
export const LABELS: Record<string, string> = { year: '년주', month: '월주', day: '일주', hour: '시주' };
export const STEMS = [...'甲乙丙丁戊己庚辛壬癸'];
export const BRANCHES = [...'子丑寅卯辰巳午未申酉戌亥'];
export const GANZHI = Array.from({ length: 60 }, (_, i) => STEMS[i % 10] + BRANCHES[i % 12]);
const clone = (v: any) => JSON.parse(JSON.stringify(v));
const empty = (status = 'missing') => ({ raw: null, normalized: null, source_refs: [], origin: null, confirmed: false, status, candidates: [] });
export interface Row {
    key: string; value: string; raw: string; source: string; confirmed: boolean;
    options: string[]; resolved: boolean; absence: string;
}
const rows = (): Row[] => POSITIONS.map(key => ({ key, value: '', raw: '', source: '직접 입력', confirmed: false, options: [], resolved: true, absence: 'missing' }));
const labelKey = (label: string) => /[년연年]/.test(label) ? 'year' : /[월月]/.test(label) ? 'month' : /[일日]/.test(label) ? 'day' : 'hour';
const tokens = (text: string) => text.replace(/천간|지지/g, '').split(/[\s|,;:/·→]+/).map(s => s.trim()).filter(Boolean);

// Explicit labels control placement; unlabeled columns never imply a position.
export function parseText(raw: string): { values: Record<string, string[]>; columns: string[]; error: string } {
    if (/\d{4}\s*(?:년|[-./])\s*\d{1,2}/.test(raw) && !/[甲乙丙丁戊己庚辛壬癸][子丑寅卯辰巳午未申酉戌亥]/.test(raw)) {
        return { values: {}, columns: [], error: '1998 같은 숫자는 생년월일입니다. 여기에는 사진에 읽힌 두 글자(예: 甲子)를 확인해 주세요. 생년월일로 사주를 임의 계산하지 않습니다.' };
    }
    const natal = raw.split(/(?:대운|세운|월운|大運|歲運|歲運表)/)[0];
    const matches = [...natal.matchAll(/(년주|연주|월주|일주|시주|年柱|月柱|日柱|時柱|년|연|월|일|시|年|月|日|時)\s*[:：]?/g)];
    const values: Record<string, string[]> = {};
    if (!matches.length) {
        const columns = tokens(natal);
        return { values, columns, error: columns.length > 4 || !columns.length ? '원국 간지 1~4개와 열 제목을 확인해 주세요. 운표는 원국과 분리해 주세요.' : '' };
    }
    // Table headers followed by a row of pairs or separate stem/branch rows.
    const headerOnly = matches.length <= 4 && matches.slice(0, -1).every((m, i) => !tokens(natal.slice(m.index! + m[0].length, matches[i + 1].index)).length);
    if (headerOnly && matches.length > 1) {
        const parts = tokens(natal.slice(matches.at(-1)!.index! + matches.at(-1)![0].length));
        const n = matches.length;
        const pairs = parts.length === n ? parts : parts.length === n * 2 && parts.every(x => x.length === 1)
            ? parts.slice(0, n).map((s, i) => s + parts[n + i]) : [];
        if (!pairs.length) return { values, columns: [], error: '열 제목과 간지 행의 개수가 다릅니다. “년주 甲子”처럼 라벨과 값을 함께 적어 주세요.' };
        matches.forEach((m, i) => (values[labelKey(m[1])] ||= []).push(pairs[i]));
    } else {
        matches.forEach((m, i) => {
            let parts = tokens(natal.slice(m.index! + m[0].length, matches[i + 1]?.index ?? natal.length));
            if (parts.length === 2 && parts.every(x => x.length === 1)) parts = [parts.join('')];
            (values[labelKey(m[1])] ||= []).push(...parts);
        });
    }
    return { values, columns: [], error: '' };
}

@Injectable({ providedIn: 'root' })
export class IntakeSession {
    public changed: () => void = () => {};
    public timing = new TimingEditor(this);
    public imageSources: any[] = [];
    public rows = rows();
    public nickname = '';
    public text = '';
    public original = '';
    public source = '';
    public columns: string[] = [];
    public mapping: string[] = [];
    public parseError = '';
    public unknown = false;
    public dirty = false;
    public composing = false;
    public token = '';
    public onUnauthorized: (token: string) => void = () => {};
    public connected = false;
    private connectedToken = '';
    public status = 'idle';
    public message = '';
    public fields: any[] = [];
    public list: any[] = [];
    public profileId = '';
    public baseId = '';
    public baseUpdatedAt = '';
    public change: any = null;
    public history: any[] = [];
    private loadSerial = 0;
    private connectSerial = 0;
    private cleanSignature = '';
    private editSignature() {
        return JSON.stringify([this.rows, this.text, this.original, this.source, this.unknown,
            this.columns, this.mapping, this.parseError, this.imageSources]);
    }
    public saved: any = null;
    public selected: any = null;
    public selectedFeature = '';
    public loaded: any = null;
    public conflict: any = null;
    public get calculationReviews(): any[] {
        const results: any[] = [];
        for (const source of this.loaded?.sources || []) {
            if (source.kind !== 'calculator') continue;
            try {
                const result = JSON.parse(source.reference);
                if (result.contract_version === 'saju-calculation/1.0.0' && result.status === 'rejected' &&
                    typeof result.raw_input === 'string' && result.validation?.status === 'unverified' &&
                    Array.isArray(result.limitations) && result.limitations.every((x: any) => typeof x === 'string') &&
                    Array.isArray(result.errors) && result.errors.every((x: any) => typeof x?.message === 'string')) results.push(result);
            } catch { /* Legacy calculator references are preserved as opaque text. */ }
        }
        return results;
    }
    public pending: any = null;
    public get local() { return ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname); }
    public get locked() { return this.status === 'saving' || !!this.pending; }
    public get hasInput() { return this.rows.some(r => !!r.value || !!r.raw) || !!this.text; }
    public get ready() {
        return !this.composing && !this.parseError && !this.columns.length && this.text === this.original &&
            this.rows.every(r => this.unknown && r.key === 'hour' ||
                !this.error(r) && !!r.value && r.confirmed && r.resolved);
    }
    public touch() { this.dirty = true; this.status = 'idle'; this.message = ''; this.fields = []; this.change = null; this.changed(); }
    public get issues(): { target: string; message: string }[] {
        const issues: { target: string; message: string }[] = [];
        if (this.parseError || this.columns.length || this.text !== this.original) issues.push({ target: this.columns.length ? 'column-0' : 'paste-text', message: this.parseError || '붙여넣은 텍스트의 후보와 열 대응을 확인해 주세요.' });
        for (const row of this.rows) {
            if (this.unknown && row.key === 'hour') continue;
            const error = this.error(row);
            if (error || !row.value) issues.push({ target: row.value && !row.source.trim() ? 'source-' + row.key : 'value-' + row.key, message: LABELS[row.key] + ': ' + (error || '시주를 입력하거나 실제 생시 미상을 확인해 주세요.') });
            else if (!row.confirmed) issues.push({ target: 'confirm-' + row.key, message: LABELS[row.key] + ' 값·출처 확인에 표시해 주세요.' });
        }
        return issues;
    }
    public error(row: Row) {
        if (this.unknown && row.key === 'hour') return '';
        if (row.value && !row.source.trim()) return '출처를 입력해 주세요.';
        if (!row.resolved) return '검토 · 후보가 여러 개입니다. 사용할 값을 명시적으로 선택해 주세요.';
        if (!row.value) return row.key === 'hour' && row.absence === 'missing' ? '' : '누락 · 값을 입력하거나 원문을 확인해 주세요.';
        if (!GANZHI.includes(row.value.trim())) return '오류 · 유효한 천간·지지의 60간지 조합을 입력해 주세요.';
        return '';
    }
    public edit(row: Row, value: string) {
        row.value = value; row.confirmed = false;
        if (!row.options.length) row.raw = this.original ? row.raw : value;
        this.touch();
    }
    public resolve(row: Row, value: string) { this.edit(row, value); row.resolved = true; }
    public parse() {
        const parsed = parseText(this.text);
        this.original = this.text; this.parseError = parsed.error;
        this.columns = parsed.error ? [] : parsed.columns; this.mapping = this.columns.map(() => '');
        this.rows = rows();
        for (const key of POSITIONS) this.apply(key, parsed.values[key] || []);
        this.touch();
    }
    private apply(key: string, values: string[]) {
        const row = this.rows.find(r => r.key === key)!;
        row.options = [...new Set(values)]; row.raw = values.join(' / ');
        row.value = row.options.length === 1 ? row.options[0] : '';
        row.resolved = row.options.length <= 1;
        row.source = this.source.trim() || '붙여넣은 텍스트';
    }
    public applyMapping() {
        if (this.mapping.some(k => !POSITIONS.includes(k)) || new Set(this.mapping).size !== this.mapping.length) {
            this.parseError = '각 열을 중복 없이 년·월·일·시주에 대응해 주세요.'; return;
        }
        this.columns.forEach((value, i) => this.apply(this.mapping[i], [value]));
        this.columns = []; this.parseError = ''; this.touch();
    }
    public reset() {
        this.loadSerial++;
        this.timing.reset();
        this.imageSources = [];
        this.rows = rows(); this.nickname = ''; this.text = ''; this.original = ''; this.source = '';
        this.columns = []; this.mapping = []; this.parseError = ''; this.unknown = false;
        this.dirty = false; this.profileId = ''; this.baseId = ''; this.loaded = null;
        this.saved = null; this.pending = null; this.conflict = null; this.status = 'idle'; this.message = ''; this.fields = [];
        this.selected = null; this.change = null; this.history = []; this.baseUpdatedAt = '';
        this.selectedFeature = '';
        this.cleanSignature = this.editSignature();
    }
    public draftInput() {
        return clone({ format: 'saju-intake-buffer-v1', nickname: this.nickname, text: this.text, original: this.original,
            source: this.source, rows: this.rows, unknown: this.unknown, columns: this.columns, mapping: this.mapping,
            parseError: this.parseError, profileId: this.profileId, baseId: this.baseId, loaded: this.loaded,
            selectedFeature: this.selectedFeature });
    }
    public restoreDraft(value: any, photoDraft = false) {
        if (photoDraft) {
            // Older photo drafts may contain only review state or a subset of the
            // text editor. Restore known fields without asserting confirmation.
            const original = value && typeof value === 'object' ? value : {};
            const emptyInput = { format: 'saju-intake-buffer-v1', nickname: '', text: '', original: '', source: '',
                parseError: '', profileId: '', baseId: '', columns: [], mapping: [], rows: rows() };
            value = { ...original, ...emptyInput };
            for (const key of ['nickname', 'text', 'original', 'source', 'parseError', 'profileId', 'baseId', 'selectedFeature']) {
                if (typeof original[key] === 'string') value[key] = original[key];
            }
            for (const key of ['columns', 'mapping']) if (Array.isArray(original[key]) && original[key].every((v: any) => typeof v === 'string')) value[key] = original[key];
            value.rows = rows().map(row => {
                const stored = Array.isArray(original.rows) ? original.rows.find((r: any) => r?.key === row.key) : null;
                if (!stored) return row;
                for (const key of ['value', 'raw', 'source', 'absence']) if (typeof stored[key] === 'string') (row as any)[key] = stored[key];
                row.options = Array.isArray(stored.options) ? stored.options.filter((s: any) => typeof s === 'string') : [];
                row.confirmed = stored.confirmed === true; row.resolved = stored.resolved === true;
                return row;
            });
            value.unknown = original.unknown === true;
            // No inferred chart from a partial editor buffer. The server review
            // and immutable profile version remain authoritative.
            value.loaded = original.loaded?.schema_version && original.loaded?.facts?.pillars ? original.loaded : null;
            if (value.selectedFeature && !/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(value.selectedFeature)) value.selectedFeature = '';
        }
        if (value?.format !== 'saju-intake-buffer-v1' || !Array.isArray(value.rows) || value.rows.length !== 4 ||
            !['nickname', 'text', 'original', 'source', 'parseError', 'profileId', 'baseId'].every(k => typeof value[k] === 'string') ||
            !['columns', 'mapping'].every(k => Array.isArray(value[k]) && value[k].every((v: any) => typeof v === 'string')) ||
            value.selectedFeature !== undefined && (typeof value.selectedFeature !== 'string' || value.selectedFeature !== '' && !/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(value.selectedFeature)) ||
            !value.rows.every((r: any, n: number) => r.key === POSITIONS[n] && ['value', 'raw', 'source', 'absence'].every(k => typeof r[k] === 'string') && Array.isArray(r.options) && r.options.every((o: any) => typeof o === 'string'))) {
            throw new Error('이 초안의 입력 형식을 복구할 수 없습니다.');
        }
        this.reset();
        for (const key of ['nickname', 'text', 'original', 'source', 'parseError', 'profileId', 'baseId', 'columns', 'mapping']) (this as any)[key] = clone(value[key]);
        this.rows = value.rows.map((r: any) => ({ key: r.key, value: r.value, raw: r.raw, source: r.source, absence: r.absence,
            options: [...r.options], confirmed: r.confirmed === true, resolved: r.resolved === true }));
        this.unknown = value.unknown === true; this.loaded = value.loaded ? clone(value.loaded) : null;
        this.selectedFeature = value.selectedFeature || '';
        this.dirty = !photoDraft;
    }
    public document() {
        const doc = this.loaded ? clone(this.loaded) : { schema_version: '1.0.0', revision_id: 'editor-draft', sources: [],
            facts: { pillars: {}, day_master: empty() }, birth: {}, birth_time_knowledge: empty(), calculations: [], interpretations: [] };
        for (const source of this.imageSources) {
            const index = doc.sources.findIndex((s: any) => s.id === source.id);
            if (index < 0) doc.sources.push(clone(source)); else doc.sources[index] = clone(source);
        }
        const id = 'input-' + crypto.randomUUID();
        // Store the exact paste independently of corrected fields in existing provenance metadata.
        doc.sources.push({ id, kind: this.original ? 'text' : 'manual', section: 'natal',
            reference: JSON.stringify({ format: 'saju-text-input-v1', text: this.original, source: this.source }) });
        for (const row of this.rows) {
            const refs = [{ source_id: id, locator: LABELS[row.key] + ' · ' + row.source }];
            let field: any = row.value ? { raw: row.raw || row.value, normalized: row.value.trim(), source_refs: refs,
                origin: 'direct_input', confirmed: row.confirmed, status: row.confirmed ? 'user_confirmed' : 'candidate', candidates: [] } : empty(row.absence);
            if (this.unknown && row.key === 'hour') field = { ...empty('excluded'), raw: row.raw || row.value || null,
                source_refs: row.raw || row.value ? refs : [], origin: row.raw || row.value ? 'direct_input' : null };
            doc.facts.pillars[row.key] = field;
        }
        const day = doc.facts.pillars.day;
        doc.facts.day_master = day.normalized ? { ...clone(day), raw: day.raw, normalized: day.normalized[0] } : empty();
        if (this.unknown) {
            doc.birth_time_knowledge = { raw: '실제 생시 미상 · 시주 제외에 동의', normalized: 'unknown', source_refs: [{ source_id: id, locator: '생시 미상 선택' }], origin: 'direct_input', confirmed: true, status: 'user_confirmed', candidates: [] };
            const exclude = (v: any): any => {
                if (Array.isArray(v)) return v.map(exclude);
                if (v && typeof v === 'object') {
                    if ('normalized' in v) return { ...v, normalized: null, confirmed: false, status: 'excluded', candidates: [] };
                    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, exclude(x)]));
                } return v;
            };
            if (doc.birth.time) doc.birth.time = exclude(doc.birth.time);
            for (const name of ['ten_gods', 'hidden_stems', 'twelve_stages', 'shensha']) if (doc.facts[name]?.hour) doc.facts[name].hour = exclude(doc.facts[name].hour);
        } else if (doc.birth_time_knowledge.normalized === 'unknown') doc.birth_time_knowledge = empty();
        return doc;
    }
    public async request(path = '', method = 'GET', body?: any) {
        requireConnection();
        const token = this.token;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
            const response = await fetch('/api/saju/profiles' + path, { method, cache: 'no-store', credentials: 'include',
                headers: { Authorization: 'Bearer ' + this.token, ...(body ? { 'Content-Type': 'application/json' } : {}) },
                body: body ? JSON.stringify(body) : undefined, signal: controller.signal });
            const result = await response.json();
            if (token !== this.token) throw { stale: true };
            if ([401,403].includes(response.status)) this.onUnauthorized(token);
            if (!response.ok) throw Object.assign(new Error(), result, { status: response.status,
                message: response.status >= 500 ? '저장 서버 응답을 확인하지 못했습니다. 작성한 내용은 유지됩니다. 같은 요청으로 다시 확인해 주세요.' : result.message || '요청을 처리하지 못했습니다.' });
            return result.data;
        } catch (error: any) {
            if (error.stale || error.status) throw error;
            throw new Error('서버 연결을 확인하지 못했습니다. 작성한 내용은 유지됩니다. 연결을 확인하고 다시 시도해 주세요.');
        } finally { clearTimeout(timeout); }
    }
    public fail(error: any) {
        if (error.stale) return;
        this.status = 'error'; this.fields = error.fields || [];
        this.message = error.message && error.status ? error.message : '서버 응답을 확인하지 못했습니다. 입력은 유지됩니다. 다시 시도해 주세요.';
    }
    public async connect() {
        const serial = ++this.connectSerial, token = this.token;
        if (this.connectedToken && this.connectedToken !== this.token && this.dirty) {
            this.fail({ status: 409, message: '작성 중인 명식을 보호하기 위해 인증 변경을 보류했습니다. 기존 인증키로 연결하거나 작성 중인 내용을 먼저 정리해 주세요.' }); return;
        }
        try { const list = await this.request();
            if (serial !== this.connectSerial || token !== this.token) return;
            if (this.connectedToken && this.connectedToken !== this.token) { this.reset(); this.selected = null; }
            this.connectedToken = this.token; this.list = list; this.connected = true; this.message = ''; this.status = 'idle'; }
        catch (e) { if (serial !== this.connectSerial || token !== this.token) return; this.connected = false; this.selected = null; this.list = []; this.fail(e); }
    }
    public async open(id: string) {
        const serial = ++this.loadSerial;
        this.selected = null;
        const data = await this.request('/' + id);
        if (serial !== this.loadSerial) return false;
        this.load(data);
        return true;
    }
    public load(data: any) {
        this.reset();
        this.profileId = data.profile.id; this.baseId = data.profile.head_revision_id;
        this.baseUpdatedAt = data.profile.updated_at;
        this.nickname = data.profile.nickname; this.loaded = clone(data.head_revision.document);
        this.saved = data.head_revision; this.selected = data.current_revision ? { profile: data.profile, revision: data.current_revision } : null;
        for (const source of this.loaded.sources) {
            try { const meta = JSON.parse(source.reference); if (meta.format === 'saju-text-input-v1') { this.original = meta.text; this.text = meta.text; this.source = meta.source; } } catch { /* Other source formats remain in the document. */ }
        }
        this.unknown = this.loaded.birth_time_knowledge.normalized === 'unknown' && this.loaded.birth_time_knowledge.confirmed;
        for (const row of this.rows) {
            const f = this.loaded.facts.pillars[row.key];
            row.raw = f.raw || ''; row.value = f.normalized || ''; row.confirmed = f.confirmed; row.absence = f.status === 'unreadable' ? 'unreadable' : 'missing';
            row.options = [...new Set<string>([f.normalized, ...f.candidates.map((x: any) => x.normalized)].filter(Boolean))];
            row.resolved = f.confirmed || (row.options.length <= 1 && f.status !== 'conflicting');
            row.source = f.source_refs.map((r: any) => r.locator || this.loaded.sources.find((s: any) => s.id === r.source_id)?.reference || '').join(' · ') || '직접 입력';
        }
        this.message = '';
        this.cleanSignature = this.editSignature();
    }
    public async save() {
        if (!this.pending && !this.ready || this.status === 'saving') return;
        if (!this.pending) {
            const body: any = { nickname: this.nickname, document: this.document(), state: 'confirmed', confirm: true, request_id: crypto.randomUUID() };
            if (this.profileId) { body.base_revision_id = this.baseId; body.base_updated_at = this.baseUpdatedAt || undefined; }
            this.pending = { path: this.profileId ? '/' + this.profileId : '', method: this.profileId ? 'PUT' : 'POST', body };
        }
        this.status = 'saving'; this.message = '명식을 확인하고 저장하고 있습니다.';
        const selected = this.selected;
        try {
            const p = this.pending;
            const rename = p.path.endsWith('/nickname');
            const chartDirty = rename && (this.editSignature() !== this.cleanSignature || this.timing.active);
            const result = await this.request(p.path, p.method, p.body);
            if (p.body.review_binding) {
                const feature = this.selectedFeature;
                this.load({ profile: result.profile, head_revision: result.revision, current_revision: result.revision });
                this.selectedFeature = feature;
            }
            this.profileId = result.profile.id; this.baseId = result.profile.head_revision_id;
            this.baseUpdatedAt = result.profile.updated_at; this.change = result.change;
            this.saved = result.revision; this.loaded = clone(result.revision.document);
            if (this.selected === selected) this.selected = result.revision.state === 'confirmed' ? { profile: result.profile, revision: result.revision } : null;
            this.pending = null; this.dirty = !!chartDirty; this.status = 'success';
            if (!rename) this.cleanSignature = this.editSignature();
            this.message = rename ? '이름을 저장했어요.' : '사주를 저장했어요.';
        } catch (e: any) {
            this.fail(e);
            if (e.status && e.status < 500) this.pending = null;
            if (e.error_code === 'REVISION_CONFLICT') this.conflict = { profile: e.current_profile };
        }
    }
    public async compare() {
        const id = this.profileId, base = this.baseId;
        try { const result = await this.request('/' + id); if (id === this.profileId && base === this.baseId) this.conflict = result; }
        catch (e) { this.fail(e); }
    }
    public useComparedBase() {
        if (!this.conflict?.head_revision) return;
        // Keep all local fields; explicit user comparison authorizes saving as a new version.
        this.baseId = this.conflict.profile.head_revision_id; this.baseUpdatedAt = this.conflict.profile.updated_at; this.conflict = null; this.pending = null;
        this.rows.forEach(r => r.confirmed = false); this.touch();
    }
    public async previewChange() {
        const id = this.profileId, base = this.baseId, token = this.token;
        const buffer = JSON.stringify(this.draftInput()), timing = JSON.stringify(this.timing.document);
        try {
            const data = await this.request('/' + id + '/impact', 'POST', { before_revision_id: base,
                document: this.timing.active ? this.timing.prepared() : this.document(), nickname: this.nickname });
            if (id === this.profileId && base === this.baseId && token === this.token && buffer === JSON.stringify(this.draftInput()) && timing === JSON.stringify(this.timing.document)) this.change = data;
        } catch (e) { if (id === this.profileId && token === this.token) this.fail(e); }
    }
    public async rename() {
        if (this.locked || !this.profileId || !this.baseUpdatedAt) return;
        this.pending = {path: '/' + this.profileId + '/nickname', method: 'PUT', body: {
            nickname: this.nickname, base_revision_id: this.baseId, base_updated_at: this.baseUpdatedAt, request_id: crypto.randomUUID()}};
        // Same retry and conflict handling as chart writes; preserve unsaved chart edits.
        await this.save();
    }
}
